import { Static, String } from "runtypes";
import { validate as isUUID } from "uuid";

import { HexId } from "./idTypeBrands.js";
import { compressUUID, decompressUUID } from "./uuidCompression.js";

// Of form "Whatever-You-Want-shortformid"
// where shortformid is the reduction of the hex uuid into a base62 string
export const HumanHexId = String.withBrand("HumanHexId");
export type HumanHexId = Static<typeof HumanHexId>;

// export const HexLocator = Union(HexId, HumanHexId);
// Normally this would be the union of HexId and HumanHexId as we need to support legacy urls on the read path
// However, since we want to encourage use of HumanHexId when generating urls, we don't want to allow HexId to be passed around
// And we create a new type HexLocator that we will "pretend" is the union of HexId and HumanHexId, and write helpers for people to get
// HexLocators so that we can automatically generate HumanHexIds for people
export const HexLocator = String.withBrand("HexLocator");
export type HexLocator = Static<typeof HexLocator>;

const isHexId = (id: string): id is HexId => {
  return isUUID(id);
};

class HexIdParseError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "HexIdParseError";
  }
}

// Format a title by:
// 1. Removing special characters
// 2. Replacing spaces with hyphens
// 3. Truncating to 100 characters
// 4. Removing any trailing dash
export const formatTitle = (title: string): string => {
  // Remove special characters, keeping only letters, numbers, spaces
  const cleaned = title.replace(/[^a-zA-Z0-9\s-]/g, "");

  // Trim spaces, replace multiple spaces with single space
  const normalized = cleaned.trim().replace(/\s+/g, " ");

  // Split on spaces, join with hyphens, truncate to 100 chars, and remove trailing dash
  return normalized.split(" ").join("-").slice(0, 100).replace(/-+$/, "");
};

// Converts a HumanHexId to a HexId
// This takes the last part after the final dash and verifies it's a valid compressed UUID
export const humanHexIdToHexId = (humanHexId: HumanHexId): HexId => {
  if (!humanHexId) {
    throw new HexIdParseError("Invalid HumanHexId: empty string");
  }

  const parts = humanHexId.split("-");
  const compressedPart = parts[parts.length - 1];

  if (!compressedPart) {
    throw new HexIdParseError(
      "Invalid HumanHexId: no compressed UUID portion found",
    );
  }

  if (compressedPart.length !== 22) {
    throw new HexIdParseError(
      `Invalid HumanHexId: "${humanHexId}". The compressed UUID portion must be exactly 22 characters.`,
    );
  }

  try {
    return decompressUUID(compressedPart) as HexId;
  } catch (_error) {
    throw new HexIdParseError(
      `Invalid HumanHexId: "${humanHexId}". The compressed UUID portion is not valid.`,
    );
  }
};

// Converts a HexId to a HumanHexId with optional prefix
export const hexIdToHumanHexId = (
  hexId: HexId,
  title: string | undefined,
): HexLocator => {
  return ((title ? `${formatTitle(title)}-` : "") +
    compressUUID(hexId)) as HexLocator;
};

export const hexIdToHumanHexIdWithoutTitle = (hexId: HexId): HexLocator => {
  return hexIdToHumanHexId(hexId, undefined);
};

// gets a hexId from a HexLocator
export const getHexId = (id: HexLocator | HexId | HumanHexId): HexId => {
  if (isHexId(id)) {
    return id;
  }
  // TODO: basically we used to have HexLocator be a union of HexId and HumanHexId
  // But we dont want to encourage passing around raw HexIds, and force people to use helpers to get the correct HexId or HumanHexId
  // so at this point we know its a HumanHexId, and we can cast it to a HumanHexId
  return humanHexIdToHexId(id as unknown as HumanHexId);
};

/**
 * Like getHexId, but returns null if the id is not a valid hex id instead of throwing.
 */
export const tryGetHexId = (id: string): HexId | null => {
  try {
    return getHexId(id as HexLocator);
  } catch (err) {
    if (err instanceof HexIdParseError) {
      return null;
    }
    throw err;
  }
};
